import { useRef, useState } from "react";
import Button from "../common/Button";
import FileViewer from "./FileViewer";
import { INPUT } from "./ExpenseModal";
import { saveAccount, importExpensesCsv } from "../../api/expenses";

// Cards and bank accounts, and whose each one is. "Whose" decides who gets
// credit: anything paid from a personal card counts as money that person put in.

export default function Accounts({ data, onChanged, onNotice, onError }) {
  const [name, setName] = useState("");
  const [owner, setOwner] = useState("company");
  const [viewing, setViewing] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const owners = ["company", ...data.partners];

  async function update(a, patch) {
    try {
      await saveAccount(a.id, { ...a, ...patch });
      onChanged();
    } catch (e) {
      onError(e.message || "Couldn't save.");
    }
  }

  async function add() {
    try {
      await saveAccount(null, { name, owner, kind: /card|amex|visa/i.test(name) ? "card" : "bank" });
      setName("");
      onChanged();
    } catch (e) {
      onError(e.message || "Couldn't add it.");
    }
  }

  async function onFile(e) {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const r = await importExpensesCsv(await f.text());
      const i = r.imported;
      onNotice(`Imported ${i.expenses} expenses, ${i.planned} to-buy items and ${i.money_in} deposits${i.skipped ? ` (${i.skipped} rows skipped)` : ""}.`);
      onChanged();
    } catch (err) {
      onError(err.message || "Couldn't import that file.");
    } finally {
      setImporting(false);
    }
  }

  return (
    <div className="space-y-5">
      <div>
        <div className="text-sm font-semibold text-birdie-charcoal">Cards and accounts</div>
        <p className="text-xs text-birdie-charcoal/55 mb-1">
          “Whose” decides who gets credit: anything paid from a personal card counts as money that person put in.
        </p>
        <ul className="divide-y divide-birdie-green/10">
          {data.accounts.map((a) => (
            <li key={a.id} className={`py-2 flex items-center gap-2 ${a.archived ? "opacity-50" : ""}`}>
              <span className="min-w-0 flex-1 text-sm text-birdie-charcoal truncate">
                {a.kind === "card" ? "💳" : "🏦"} {a.name}
              </span>
              <select
                value={a.owner}
                onChange={(e) => update(a, { owner: e.target.value })}
                className="rounded-lg border border-birdie-green/20 bg-white px-2 py-1 text-xs"
                aria-label={`Whose is ${a.name}`}
              >
                {owners.map((o) => (
                  <option key={o} value={o}>
                    {o === "company" ? "Company" : `${o}'s`}
                  </option>
                ))}
              </select>
              <button type="button" onClick={() => update(a, { archived: !a.archived })} className="text-[11px] text-birdie-charcoal/50 shrink-0">
                {a.archived ? "Restore" : "Hide"}
              </button>
            </li>
          ))}
        </ul>
        <div className="flex gap-2 mt-3">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New card or account" className={`${INPUT} min-w-0 flex-1`} />
          <select
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            className="w-28 shrink-0 rounded-xl border border-birdie-green/20 bg-white px-2 py-2 text-sm"
            aria-label="Whose"
          >
            {owners.map((o) => (
              <option key={o} value={o}>
                {o === "company" ? "Company" : `${o}'s`}
              </option>
            ))}
          </select>
          <Button size="sm" onClick={add} disabled={!name.trim()}>
            Add
          </Button>
        </div>
      </div>

      {data.statements.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-birdie-charcoal mb-1">Scanned statements</div>
          <ul className="divide-y divide-birdie-green/10">
            {data.statements.map((s) => (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setViewing({ id: s.id, filename: s.filename, content_type: /\.pdf$/i.test(s.filename) ? "application/pdf" : "image/jpeg" })}
                  className="w-full flex justify-between gap-2 py-2 text-left text-sm"
                >
                  <span className="truncate text-birdie-green font-medium">📄 {s.filename}</span>
                  <span className="shrink-0 text-[11px] text-birdie-charcoal/50">{s.rows} added</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <div className="text-sm font-semibold text-birdie-charcoal">Import a spreadsheet</div>
        <p className="text-xs text-birdie-charcoal/55 mb-2">
          A CSV with columns like type, date, description, amount, category, paid_with, partner, notes.
        </p>
        <Button variant="secondary" size="sm" onClick={() => fileRef.current?.click()} disabled={importing}>
          {importing ? "Importing…" : "Choose CSV"}
        </Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
      </div>

      {viewing && <FileViewer file={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}
